import PageHead from "@/components/PageHead";
import { useLocation } from "@/lib/router-compat";
import { SITE_URL, SITE_NAME, DEFAULT_OG_IMAGE } from "@/config/site";

interface SEOHeadProps {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  noIndex?: boolean;
  ogType?: string;
  /** Extra head children such as JSON-LD (<JsonLd data={...} />) */
  children?: React.ReactNode;
}

/**
 * Unified SEO <head> component: title, description, canonical, robots,
 * Open Graph and Twitter Card tags.
 *
 * The SSR copy of these tags comes from the route's `head()` option
 * (src/lib/seo/routeHead.ts, fed by src/generated/seoRouteMeta.ts). This
 * component keeps the live document in sync on client-side navigation.
 */
const SEOHead = ({
  title,
  description,
  canonical,
  ogImage,
  noIndex = false,
  ogType = "website",
  children,
}: SEOHeadProps) => {
  const { pathname } = useLocation();

  // Build canonical from pathname if not explicitly provided
  const normPath = pathname === "/" ? "/" : pathname.replace(/\/$/, "");
  const resolvedCanonical = canonical || `${SITE_URL}${normPath}`;
  // Resolve relative / bundler-hashed image URLs to absolute so social
  // crawlers (which don't run a browser) can fetch them.
  const rawImage = ogImage || DEFAULT_OG_IMAGE;
  const resolvedImage = /^https?:\/\//i.test(rawImage)
    ? rawImage
    : `${SITE_URL}${rawImage.startsWith("/") ? "" : "/"}${rawImage}`;

  return (
    <>
      <PageHead
        title={title}
        description={description}
        canonical={resolvedCanonical}
        meta={[
          { name: "robots", content: noIndex ? "noindex, nofollow" : "index, follow" },
          { property: "og:type", content: ogType },
          { property: "og:url", content: resolvedCanonical },
          { property: "og:title", content: title },
          { property: "og:description", content: description },
          { property: "og:image", content: resolvedImage },
          { property: "og:image:width", content: "1200" },
          { property: "og:image:height", content: "630" },
          { property: "og:site_name", content: SITE_NAME },
          { name: "twitter:card", content: "summary_large_image" },
          { name: "twitter:title", content: title },
          { name: "twitter:description", content: description },
          { name: "twitter:image", content: resolvedImage },
        ]}
      />
      {children}
    </>
  );
};

export default SEOHead;
